import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowRight, ArrowLeft, Shield, FileText, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import logo from '../assets/images/base.png';
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import {
  ConsentType,
  ConsentValidationResult,
  ConsentSummary,
  getAllRequiredConsents,
  getRequiredConsentsForUserType,
  CONSENT_FLOWS
} from "@/types/consent";

interface ConsentState {
  [key: string]: boolean;
}

interface ScrollState {
  [key: string]: boolean;
}

export default function ConsentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, checkAuth } = useAuthStore();
  
  // Get redirect URL from query params
  const redirectTo = searchParams.get("redirect") || "/onboarding-v2";
  const action = searchParams.get("action") || "basic_access";
  
  // Helper function to check if user is a brand user
  // Also check if this is a brand signup flow by looking at the redirect URL
  const isBrandSignupFlow = redirectTo.includes('brand-onboarding');
  const isBrandUser = user?.role === 'brand_user' || user?.brand_id || isBrandSignupFlow;
  
  const [consentStates, setConsentStates] = useState<ConsentState>({});
  const [scrollStates, setScrollStates] = useState<ScrollState>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState<ConsentValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requiredConsents, setRequiredConsents] = useState<ConsentType[]>([]);
  const [allAccepted, setAllAccepted] = useState(false);

  useEffect(() => {
    // Initialize consent flow when component mounts
    // Empty dependency array ensures this runs only once
    initializeConsentFlow();
  }, []); // Empty dependency array to run only once

  const initializeConsentFlow = async () => {
    try {
      setIsLoading(true);
      
      // DEBUG: Add logging to track API calls
      console.log("🔄 ConsentPage: initializeConsentFlow called", {
        timestamp: new Date().toISOString(),
        action,
        userId: user?._id,
        userRole: user?.role,
        isBrandSignupFlow,
        redirectTo
      });
      
      // Get current consent validation status
      console.log("🌐 ConsentPage: Making API call to validateConsents");
      const validation = await apiClient.validateConsents(action) as ConsentValidationResult;
      setValidationResult(validation);
      console.log("✅ ConsentPage: validateConsents successful", { isValid: validation.is_valid });

      // Get required consents based on user type (brands get different consents than creators)
      // Determine user type based on brand_id, role, or signup flow
      const userType = user?.brand_id || isBrandSignupFlow ? 'brand' : 'creator';
      
      const required = getRequiredConsentsForUserType(userType, user?.role, user?.brand_id);
      setRequiredConsents(required);
      console.log("📋 ConsentPage: Required consents determined", { userType, required });
      
      // Get user's current consent summary
      console.log("🌐 ConsentPage: Making API call to getConsentSummary");
      const summary = await apiClient.getConsentSummary() as ConsentSummary;
      console.log("✅ ConsentPage: getConsentSummary successful");

      // Initialize consent states based on current status
      const initialStates: ConsentState = {};
      const initialScrollStates: ScrollState = {};
      required.forEach(consentType => {
        initialStates[consentType] = summary.consents[consentType]?.status === "accepted";
        // If already accepted, mark as scrolled
        initialScrollStates[consentType] = summary.consents[consentType]?.status === "accepted";
      });
      setConsentStates(initialStates);
      setScrollStates(initialScrollStates);

      // If all consents are already completed, redirect
      const allCompleted = required.every(consentType =>
        summary.consents[consentType]?.status === "accepted"
      );
      
      if (allCompleted && validation.is_valid) {
        // For brand onboarding flow, don't auto-redirect to prevent glitch
        // Let user manually proceed or go back
        if (!isBrandSignupFlow) {
          handleAllConsentsCompleted();
        } else {
           // If brand user comes back to consent page but is already done,
           // we can offer to continue but maybe not auto-redirect immediately to avoid loops
           // if they explicitly clicked "back".
           // However, handleAllConsentsCompleted handles the navigation correctly now.
        }
      }
    } catch (error) {
      console.error("Error initializing consent flow:", error);
      setError("Failed to load consent requirements");
      toast.error("Failed to load consent requirements");
    } finally {
      setIsLoading(false);
    }
  };

  const handleScroll = (consentType: ConsentType, event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    
    const scrolled = (scrollTop / (scrollHeight - clientHeight)) * 100;
    
    // Consider "scrolled to bottom" when user reaches 95% or more
    if (scrolled >= 95) {
      setScrollStates(prev => ({
        ...prev,
        [consentType]: true
      }));
    }
  };

  const handleConsentChange = (consentType: ConsentType, checked: boolean) => {
    // Only allow checking if user has scrolled to bottom (for documents with scrollable content)
    const config = CONSENT_FLOWS[consentType];
    const hasScrollableDocument = config.documentContent && !config.documentUrl;
    
    if (hasScrollableDocument && !scrollStates[consentType] && checked) {
      toast.error("Please scroll to the bottom of the document before accepting");
      return;
    }
    
    setConsentStates(prev => ({
      ...prev,
      [consentType]: checked
    }));
    setAllAccepted(Object.values(consentStates).every(Boolean));
  };

  const handleAcceptAllChange = (checked: boolean) => {
    setAllAccepted(checked);
    const newConsentStates: ConsentState = {};
    requiredConsents.forEach(consentType => {
      newConsentStates[consentType] = checked;
    });
    setConsentStates(newConsentStates);
  };

  const handleSubmit = async () => {
    // Check if all required consents are accepted
    const allRequiredAccepted = requiredConsents.every(consentType => 
      consentStates[consentType] === true
    );

    if (!allRequiredAccepted) {
      toast.error("Please accept all required agreements to continue");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Submit all consents
      for (const consentType of requiredConsents) {
        if (consentStates[consentType]) {
          switch (consentType) {
            case ConsentType.TERMS_OF_SERVICE:
              await apiClient.acceptTermsOfService(100, 0, "v1.0");
              break;
            case ConsentType.PRIVACY_POLICY:
              await apiClient.acceptPrivacyPolicy("v1.0");
              break;
            case ConsentType.AGE_VERIFICATION:
              await apiClient.verifyAge();
              break;
            case ConsentType.AI_PROCESSING:
              await apiClient.acceptAiProcessing();
              break;
            case ConsentType.DATA_SHARING:
              await apiClient.acceptDataSharing();
              break;
          }
        }
      }

      await handleAllConsentsCompleted();
    } catch (error) {
      console.error("Error submitting consents:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to submit agreements";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAllConsentsCompleted = async () => {
    try {
      console.log("🎯 ConsentPage: handleAllConsentsCompleted called", {
        timestamp: new Date().toISOString(),
        isBrandSignupFlow,
        redirectTo
      });
      
      // Remove checkAuth() call to prevent race condition with BrandOnboardingStep1
      // The BrandOnboardingContext will handle status updates automatically
      console.log("✅ ConsentPage: Skipping checkAuth() to prevent glitches");
      
      toast.success("All required agreements accepted!");
      
      // For brand onboarding flow, ensure we navigate to the company info step (Step 1)
      if (isBrandSignupFlow) {
        console.log("🧭 ConsentPage: Navigating to brand onboarding company-info");
        // Reduce delay and use immediate navigation to prevent glitches
        setTimeout(() => {
          navigate("/brand-onboarding/company-info", { replace: true });
        }, 200);
      } else {
        console.log("🧭 ConsentPage: Navigating to redirectTo", { redirectTo });
        // Add a small delay for smoother transition
        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 200);
      }
    } catch (error) {
      console.error("❌ ConsentPage: Error completing consent flow:", error);
      toast.error("Error completing consent flow");
    }
  };

  const renderDocumentContent = (consentType: ConsentType) => {
    const config = CONSENT_FLOWS[consentType];
    
    // For documents with inline content, show it
    if (config.documentContent) {
      return (
        <div
          className="prose prose-sm max-w-none text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: config.documentContent }}
        />
      );
    }

    // For Terms of Service, show link to Creator Agreement
    if (consentType === ConsentType.TERMS_OF_SERVICE) {
      return (
        <div className="bg-gradient-to-br from-[oklch(0.98_0.01_60)] to-[oklch(0.96_0.02_55)] p-6 rounded-lg border border-[oklch(0.90_0.02_55)]">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-[oklch(0.70_0.18_55)] rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold mb-2 text-[oklch(0.2686_0_0)]">
                {isBrandUser ? 'Brand Agreement' : 'Creator Agreement'}
              </h4>
              <p className="text-sm text-[oklch(0.55_0_0)] mb-4 leading-relaxed">
                {isBrandUser
                  ? 'Our comprehensive agreement covering services, deliverables, payment terms, intellectual property, and all legal terms for brand partnerships.'
                  : 'Our comprehensive agreement covering services, fees, licenses, creator commitments, and all legal terms for brand partnerships.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => window.open(isBrandUser ? '/brand-agreement' : '/creator-agreement', '_blank')}
                  className="bg-[oklch(0.70_0.18_55)] hover:bg-[oklch(0.65_0.18_55)] text-white flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Read Full Agreement
                </Button>
                <div className="text-xs text-[oklch(0.55_0_0)] flex items-center gap-1">
                  <span className="w-2 h-2 bg-[oklch(0.70_0.18_55)] rounded-full"></span>
                  Opens in new tab
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // For Privacy Policy, show link to Privacy Policy document
    if (consentType === ConsentType.PRIVACY_POLICY) {
      return (
        <div className="bg-gradient-to-br from-[oklch(0.98_0.01_60)] to-[oklch(0.96_0.02_55)] p-6 rounded-lg border border-[oklch(0.90_0.02_55)]">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-[oklch(0.70_0.18_55)] rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold mb-2 text-[oklch(0.2686_0_0)]">Privacy Policy</h4>
              <p className="text-sm text-[oklch(0.55_0_0)] mb-4 leading-relaxed">
                Learn how we collect, use, and protect your personal information, including data sharing with brand partners and your privacy rights.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => window.open('/privacy-policy', '_blank')}
                  className="bg-[oklch(0.70_0.18_55)] hover:bg-[oklch(0.65_0.18_55)] text-white flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Read Privacy Policy
                </Button>
                <div className="text-xs text-[oklch(0.55_0_0)] flex items-center gap-1">
                  <span className="w-2 h-2 bg-[oklch(0.70_0.18_55)] rounded-full"></span>
                  Opens in new tab
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
        <main className="relative z-10 w-full flex flex-col items-center min-h-screen justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[oklch(0.70_0.18_55)] border-t-transparent" />
            <p className="text-sm text-[oklch(0.55_0_0)]">Loading legal agreements...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
        <main className="relative z-10 w-full flex flex-col items-center min-h-screen justify-center">
          <Card className="w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <h2 className="text-lg font-semibold">Error</h2>
            </div>
            <p className="text-sm text-[oklch(0.55_0_0)] mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} className="w-full">
              Try Again
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  const allRequiredAccepted = requiredConsents.every(consentType =>
    consentStates[consentType] === true
  );

  // Check if all consents are already completed from previous steps
  // Use validationResult.is_valid to determine if all required consents are completed
  const allConsentsAlreadyCompleted = validationResult?.is_valid === true;

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">
        <div className="w-full max-w-5xl px-6 py-12 flex flex-col items-center flex-grow">
          {/* Logo */}
          <div className="mb-8 relative">
            <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
            <img
              src={logo}
              alt="Artie"
              className="w-32 h-32 object-contain relative z-10"
            />
          </div>

          {/* Header */}
          <div className="text-center max-w-2xl mb-12 animate-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              Legal Agreements
            </h1>
            <div className="space-y-4 text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed">
              <p>
                Before we begin, please review and accept the following agreements to continue using Artie.
              </p>
            </div>
          </div>

          {/* Show completion message for brand users when all consents are already completed */}
          {isBrandSignupFlow && allConsentsAlreadyCompleted && (
            <div className="w-full max-w-4xl mb-8">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">All Agreements Completed</h3>
                </div>
                <p className="text-green-700 mb-4">
                  You have already accepted all required legal agreements.
                  You can proceed to complete your brand onboarding or review the agreements below.
                </p>
                <Button
                  onClick={() => navigate("/brand-onboarding/company-info")}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Continue to Company Info
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Agreements */}
          <div className="w-full max-w-4xl space-y-6 mb-8">
            <Card className="bg-white/80 backdrop-blur border border-[oklch(0.92_0_0)] shadow-sm">
              <div className="p-6">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="accept-all"
                    checked={allAccepted}
                    onCheckedChange={(checked) => handleAcceptAllChange(checked as boolean)}
                  />
                  <label
                    htmlFor="accept-all"
                    className="text-sm font-medium leading-relaxed cursor-pointer flex items-center"
                  >
                    Accept All Agreements
                  </label>
                </div>
              </div>
            </Card>
            {requiredConsents.map((consentType) => {
              const config = CONSENT_FLOWS[consentType];
              const isAccepted = consentStates[consentType] || false;
              
              return (
                <Card key={consentType} className="bg-white/80 backdrop-blur border border-[oklch(0.92_0_0)] shadow-sm">
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 mt-1">
                        <Shield className="w-5 h-5 text-[oklch(0.70_0.18_55)]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-[oklch(0.2686_0_0)] mb-2 font-cabinet">
                          {config.title}
                          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full ml-2">
                            Required
                          </span>
                        </h3>
                        <p className="text-sm text-[oklch(0.55_0_0)] font-ibm mb-4">
                          {config.description}
                        </p>
                      </div>
                    </div>

                    {/* Document Content */}
                    {(config.documentContent || config.documentUrl) && (
                      <div className="mb-4">
                        {config.documentContent ? (
                          // Scrollable content for inline documents
                          <>
                            <ScrollArea
                              className="h-48 w-full border border-[oklch(0.92_0_0)] rounded-md p-4 bg-[oklch(0.98_0_0)]"
                              onScrollCapture={(e) => handleScroll(consentType, e)}
                            >
                              {renderDocumentContent(consentType)}
                            </ScrollArea>
                            {!scrollStates[consentType] && (
                              <p className="text-xs text-[oklch(0.55_0_0)] mt-2 flex items-center gap-1">
                                <ArrowRight className="w-3 h-3" />
                                Please scroll to the bottom to enable the checkbox
                              </p>
                            )}
                          </>
                        ) : (
                          // Document link for external documents
                          <div className="border border-[oklch(0.92_0_0)] rounded-md p-4 bg-[oklch(0.98_0_0)]">
                            {renderDocumentContent(consentType)}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Consent Checkbox */}
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id={`consent-${consentType}`}
                        checked={isAccepted}
                        onCheckedChange={(checked) => handleConsentChange(consentType, checked as boolean)}
                        disabled={config.documentContent && !scrollStates[consentType]}
                      />
                      <label
                        htmlFor={`consent-${consentType}`}
                        className={`text-sm font-medium leading-relaxed cursor-pointer flex items-center ${
                          config.documentContent && !scrollStates[consentType]
                            ? "text-[oklch(0.55_0_0)]"
                            : "text-[oklch(0.2686_0_0)]"
                        }`}
                      >
                        {config.checkboxText}
                        {scrollStates[consentType] && config.documentContent && (
                          <CheckCircle className="w-4 h-4 text-green-600 ml-2" />
                        )}
                      </label>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

        </div>

        {/* Footer & Navigation */}
        <div className="w-full mt-auto pb-10 px-20">
          <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
          <div className="w-full flex items-center justify-between">
            <div className="flex-1 flex justify-start">
              {/* For brand signup flow (where this is effectively Step 0), Back goes to login or signup if needed, but usually hidden/disabled or goes to "/" */}
              {/* If not brand signup flow, keep existing behavior */}
              <Button
                variant="ghost"
                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                onClick={() => isBrandSignupFlow ? navigate("/signup") : navigate("/")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            <div className="flex-1 flex justify-end">
              <Button
                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={allConsentsAlreadyCompleted ? () => navigate(isBrandSignupFlow ? "/brand-onboarding/company-info" : redirectTo) : handleSubmit}
                disabled={(!allRequiredAccepted && !allConsentsAlreadyCompleted) || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    Accepting... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                  </>
                ) : allConsentsAlreadyCompleted ? (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    Accept All & Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}